import { Connection } from "@zapier/client";

export const STALE_SUSPECT_DAYS = 10;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDate = (value: string | null | undefined): Date | undefined => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

// `lastchanged` is folded in because re-auth bumps it (but not `lastTestedAt`),
// so without it freshly re-authed connections would trigger the suspect cue.
// Trade-off: `lastchanged` also moves on renames and other edits with no
// freshness signal, so a recent rename can silence the cue for up to
// STALE_SUSPECT_DAYS. `connection.date` is a last-resort fallback only.
// Both proxies will be replaced by the `lastused` field from the connection
// metadata database once it's populated end-to-end.
export const getStaleReferenceDate = (
  connection: Pick<Connection, "lastTestedAt" | "lastchanged" | "date">,
): Date | undefined => {
  const lastTested = parseDate(connection.lastTestedAt);
  const lastChanged = parseDate(connection.lastchanged);

  if (lastTested && lastChanged) {
    return new Date(Math.max(lastTested.getTime(), lastChanged.getTime()));
  }
  return lastTested ?? lastChanged ?? parseDate(connection.date);
};

export function isStaleSuspect(
  connection: Pick<
    Connection,
    "isStale" | "lastTestedAt" | "lastchanged" | "date"
  >,
  now: Date = new Date(),
): boolean {
  if (connection.isStale) return false;

  const reference = getStaleReferenceDate(connection);
  if (!reference) return false;

  const ageDays = (now.getTime() - reference.getTime()) / MS_PER_DAY;
  return ageDays >= STALE_SUSPECT_DAYS;
}
